import './LoiFormsTab.scss';
import React, { useEffect, useState } from 'react';
import BootstrapTable from '../../../components/react-bootstrap-table-next/react-bootstrap-table2';
import { useStateSelector } from '../../../store/selectors';
import createNotification from '../../../utils/createNotification';
import {
    fetchLoiFormsForReview,
    fetchLoiFormsByIds,
    submitAdminLoiForms,
    LoiFormForReviewContract,
} from '../../../slices/marketplace/loi-form/loi-forms-api';
import { TimelineEvent } from '../../../slices/marketplace/timeline/models/timeline-event.model';
import { TimelineActionType } from '../../../slices/marketplace/timeline/models/timeline-action-type.model';
import { TimelineEventStatus } from '../../../slices/marketplace/timeline/models/enums/timeline-event-status.enum';
import { validateLoiForm, hasLoiFormErrors } from '../../../slices/marketplace/loi-form/loi-form-validator';
import { LoiFormReviewModal } from './LoiFormReviewModal';
import { LoiPreparationModal } from './LoiPreparationModal';
import { Button } from 'reactstrap';
import ConfirmationModal from '../../../components/Modal/ConfirmationModal';
import BuyerProfilePreviewPopup from '../../../components/BuyerProfilePreviewPopup/BuyerProfilePreviewPopup';
import Helpers from '../../../utils/helper';

export interface LoiFormsTabProps {
    engagementId: number;
    events: TimelineEvent[];
    levelOfService?: number;
    isAdmin?: boolean;
    engagedBuyerId?: number | null;
}

const LOI_STATUS_CONFIRMED = 'Confirmed by Buyer';
const LOI_STATUS_UNDER_REVIEW = 'Under Review';
const LOI_STATUS_BUYER_SUBMITTED = 'Buyer Submitted';
const LOI_STATUS_UPCOMING = 'Upcoming';

const SUBMIT_ALL_CONFIRMATION_TEXT = (
    <span>Are you sure you want to submit all LOI forms? This will make them available to buyers for review.</span>
);

export const LoiFormsTab = (props: LoiFormsTabProps) => {
    const axios = useStateSelector((state) => state.core.axios);

    const [loiForms, setLoiForms] = useState<LoiFormForReviewContract[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedLoiForm, setSelectedLoiForm] =
        useState<LoiFormForReviewContract | null>(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isConfirmVisible, setIsConfirmVisible] = useState(false);
    const [selectedBuyerId, setSelectedBuyerId] = useState<number | null>(null);
    const [isBuyerProfileVisible, setIsBuyerProfileVisible] = useState(false);
    const [isLoiPrepModalVisible, setIsLoiPrepModalVisible] = useState(false);
    const [loiPrepBuyerId, setLoiPrepBuyerId] = useState<number | null>(null);
    const [loiPrepEventId, setLoiPrepEventId] = useState<string | null>(null);

    const loiCreationEvent = props.events.find(
        (e) =>
            e.linkedAction?.actionType ===
            TimelineActionType.VetValueLoiFormCreation
    );
    const eventId = loiCreationEvent?.id ?? '';
    const isAdminEventActive =
        loiCreationEvent != null &&
        loiCreationEvent.status !== TimelineEventStatus.Upcoming;

    const loiPrepEvent = props.events.find(
        (e) =>
            e.linkedAction?.actionType ===
                TimelineActionType.SellerCustomSelectLoiAndDdNdaTerms &&
            e.status !== TimelineEventStatus.Upcoming
    );

    const loadLoiForms = () => {
        if (!axios) {
            return;
        }
        setIsLoading(true);
        fetchLoiFormsForReview(axios, props.engagementId)
            .then((data) => {
                setLoiForms(data);
            })
            .catch(() => {
                createNotification(
                    'Could not load LOI forms. Please try again.',
                    'error'
                );
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    useEffect(() => {
        loadLoiForms();
    }, [props.engagementId]);

    const handleDownloadLoi = (documentDetailsId: string | null) => {
        if (!documentDetailsId || !axios) {
            return;
        }
        axios
            .get(
                `/api/marketplace/${props.engagementId}/MarketplaceOpportunityDocuments/${documentDetailsId}`
            )
            .then((response: { data: string }) => {
                window.open(response.data, '_blank');
            })
            .catch(() => {
                createNotification(
                    'Could not download LOI file. Please try again.',
                    'error'
                );
            });
    };

    const handleOpenModal = (loiForm: LoiFormForReviewContract) => {
        setSelectedLoiForm(loiForm);
        setIsModalVisible(true);
    };

    const handleCloseModal = () => {
        setIsModalVisible(false);
        setSelectedLoiForm(null);
    };

    const handleSubmitComplete = () => {
        handleCloseModal();
        loadLoiForms();
    };

    const handleOpenBuyerProfile = (buyerId: number) => {
        setSelectedBuyerId(buyerId);
        setIsBuyerProfileVisible(true);
    };

    const handleCloseBuyerProfile = () => {
        setIsBuyerProfileVisible(false);
        setSelectedBuyerId(null);
    };

    const regularForms = loiForms.filter(f => !f.isResubmission);
    const allFormsComplete = regularForms.length > 0 && regularForms.every((f) => f.isAdminSubmitted);

    const handleSubmitAllClick = () => {
        setIsConfirmVisible(true);
    };

    const handleSubmitAllConfirm = async () => {
        if (!axios || !eventId) return;
        setIsSubmitting(true);
        try {
            const loiFormIds = regularForms.map((f) => f.loiFormId);
            const fullForms = await fetchLoiFormsByIds(axios, loiFormIds);

            const incompleteBuyerNames: string[] = [];
            for (const reviewForm of loiForms) {
                const fullForm = fullForms.find((f) => f.id === reviewForm.loiFormId);
                if (!fullForm) { continue; }
                const errors = validateLoiForm(fullForm, { documentDetailsId: reviewForm.documentDetailsId ?? undefined, isFsb: props.levelOfService === 1, isAdmin: props.isAdmin, skipLegalTabs: true });
                if (hasLoiFormErrors(errors)) {
                    incompleteBuyerNames.push(reviewForm.buyerName);
                }
            }

            if (incompleteBuyerNames.length > 0) {
                createNotification(
                    `Cannot submit: LOI forms for ${incompleteBuyerNames.join(', ')} are incomplete. Please review and fill all required fields.`,
                    'error'
                );
                setIsConfirmVisible(false);
                return;
            }

            await submitAdminLoiForms(axios, props.engagementId, eventId, loiFormIds);
            createNotification('All LOI forms have been submitted successfully.', 'success');
            setIsConfirmVisible(false);
            loadLoiForms();
        } catch {
            createNotification('Could not submit LOI forms. Please try again.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleOpenLoiPrepModal = (buyerId: number, prepEventId: string) => {
        setLoiPrepBuyerId(buyerId);
        setLoiPrepEventId(prepEventId);
        setIsLoiPrepModalVisible(true);
    };

    const handleCloseLoiPrepModal = () => {
        setIsLoiPrepModalVisible(false);
        setLoiPrepBuyerId(null);
        setLoiPrepEventId(null);
        loadLoiForms();
    };

    const formatDeadline = (deadline: string | null): string => {
        if (!deadline) {
            return '—';
        }
        return new Date(deadline).toLocaleDateString();
    };

    const getLoiFormStatus = (loiForm: LoiFormForReviewContract): string => {
        if (loiForm.isBuyerConfirmed) {
            return LOI_STATUS_CONFIRMED;
        }
        if (loiForm.isAdminSubmitted) {
            return LOI_STATUS_UNDER_REVIEW;
        }
        if (loiForm.isBuyerSubmitted) {
            return LOI_STATUS_BUYER_SUBMITTED;
        }
        return LOI_STATUS_UPCOMING;
    };

    const getColumns = () => [
        {
            dataField: 'buyerId',
            text: 'Id',
            hidden: true,
        },
        {
            dataField: 'buyerName',
            text: 'Buyer',
            sort: true,
            headerClasses: 'buyer-column-header',
            formatter: (cell: string, row: LoiFormForReviewContract) => (
                <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        handleOpenBuyerProfile(row.buyerId);
                    }}>
                    {cell}
                </a>
            ),
        },
        {
            dataField: 'documentDetailsId',
            text: 'LOI Link',
            headerClasses: 'loi-link-column-header',
            formatter: (_cell: string | null, row: LoiFormForReviewContract) => {
                if (!row.documentDetailsId) {
                    return null;
                }
                return (
                    <a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            handleDownloadLoi(row.documentDetailsId);
                        }}>
                        {row.documentName || 'LOI Document'}
                    </a>
                );
            },
        },
        {
            dataField: 'isAdminSubmitted',
            text: 'Status',
            headerClasses: 'status-column-header',
            formatter: (_cell: boolean, row: LoiFormForReviewContract) =>
                getLoiFormStatus(row),
        },
        {
            dataField: 'deadline',
            text: 'Deadline',
            headerClasses: 'deadline-column-header',
            formatter: (cell: string | null) => formatDeadline(cell),
        },
        {
            dataField: 'loiFormId',
            text: 'Action',
            headerClasses: 'action-column-header',
            formatter: (_cell: string, row: LoiFormForReviewContract) => {
                const showPrepButton =
                    props.isAdmin &&
                    loiPrepEvent != null &&
                    props.engagedBuyerId != null &&
                    row.buyerId === props.engagedBuyerId &&
                    row.isLoiPrepared !== true;
                return (
                    <div className="d-flex flex-column align-items-start" style={{ gap: '8px' }}>
                        <Button
                            color="primary"
                            size="sm"
                            disabled={false}
                            onClick={() => handleOpenModal(row)}>
                            View / Edit LOI Form
                        </Button>
                        {showPrepButton && (
                            <Button
                                color="primary"
                                size="sm"
                                onClick={() =>
                                    handleOpenLoiPrepModal(row.buyerId, loiPrepEvent!.id)
                                }>
                                Prepare LOI for signing
                            </Button>
                        )}
                    </div>
                );
            },
        },
    ];

    if (isLoading) {
        return (
            <div className="loi-forms-tab">
                {Helpers.renderTableLoader()}
            </div>
        );
    }

    return (
        <div className="loi-forms-tab">
            <BootstrapTable
                striped
                bordered
                bootstrap4
                keyField="loiFormId"
                data={loiForms}
                columns={getColumns()}
                noDataIndication="No LOI forms available"
            />
            <div className="loi-forms-tab-actions">
                <Button
                    color="primary"
                    disabled={isSubmitting || allFormsComplete || loiForms.length === 0 || !isAdminEventActive}
                    onClick={handleSubmitAllClick}>
                    {isSubmitting ? 'Submitting...' : 'Submit All'}
                </Button>
            </div>

            {isModalVisible && selectedLoiForm && (
                <LoiFormReviewModal
                    isVisible={isModalVisible}
                    onClose={handleCloseModal}
                    engagementId={props.engagementId}
                    loiFormId={selectedLoiForm.loiFormId}
                    eventId={eventId}
                    documentDetailsId={selectedLoiForm.documentDetailsId}
                    documentName={selectedLoiForm.documentName}
                    buyerName={selectedLoiForm.buyerName}
                    onSubmitComplete={handleSubmitComplete}
                    levelOfService={props.levelOfService}
                    loiProcessStep={selectedLoiForm.loiProcessStep}
                    buyerId={selectedLoiForm.buyerId}
                    isResubmission={selectedLoiForm.isResubmission}
                    loiFormsProcesses={selectedLoiForm.loiFormsProcesses}
                    isAdminEventActive={isAdminEventActive}
                />
            )}
            {isBuyerProfileVisible && selectedBuyerId && (
                <BuyerProfilePreviewPopup
                    buyerId={selectedBuyerId}
                    close={handleCloseBuyerProfile}
                />
            )}
            <ConfirmationModal
                isVisible={isConfirmVisible}
                setIsVisible={setIsConfirmVisible}
                header="Submit All LOI Forms"
                confirmationText={SUBMIT_ALL_CONFIRMATION_TEXT}
                onConfirm={handleSubmitAllConfirm}
                nextButtonText="Confirm"
                cancelButtonText="Cancel"
                isLoading={isSubmitting}
                closeOnConfirm={false}
            />
            {isLoiPrepModalVisible && loiPrepBuyerId != null && loiPrepEventId != null && (
                <LoiPreparationModal
                    isVisible={isLoiPrepModalVisible}
                    onClose={handleCloseLoiPrepModal}
                    engagementId={props.engagementId}
                    eventId={loiPrepEventId}
                    buyerId={loiPrepBuyerId}
                />
            )}
        </div>
    );
};
